import { Layout } from "@/components/Layout";
import { Link } from "react-router-dom";
import { SeoHead } from "@/components/SeoHead";
import { JsonLd, breadcrumbSchema, serviceSchema } from "@/components/JsonLd";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";
import { allGeoTowns } from "@/data/geoPages";

// Photo imports with SEO filenames
import wellCasingImg from "@/assets/well-casing-pump-installation.webp";
import wellPullingImg from "@/assets/well-pump-pulling-equipment-repair.webp";
import grinderInstallImg from "@/assets/grinder-pump-installation-excavator.webp";
import sewagePumpImg from "@/assets/sewage-pump-basin-installation.webp";
import openWellImg from "@/assets/open-well-pump-repair-poconos.webp";
import truckImg from "@/assets/semper-fi-well-pump-service-truck.webp";
import pumpStationImg from "@/assets/sewage-pump-station-gravel-base.webp";
import buriedPumpImg from "@/assets/grinder-pump-basin-buried-installation.webp";
import crewPullingImg from "@/assets/technicians-pulling-well-pump-teamwork.webp";
import gravelBackfillImg from "@/assets/grinder-pump-gravel-backfill-install.webp";
import crewInstallingImg from "@/assets/crew-installing-pump-basin-underground.webp";
import pumpCloseupImg from "@/assets/sewage-grinder-pump-unit-closeup.webp";
import excavatorDigImg from "@/assets/kubota-excavator-well-pump-excavation.webp";
import pumpBasinPipingImg from "@/assets/grinder-pump-basin-piping-connections.webp";

interface ServicePageData {
  title: string;
  pageTitle: string;
  metaDesc: string;
  h1: string;
  intro: string;
  heroImg: string;
  heroAlt: string;
  path: string;
  symptoms: string[];
  causes: string[];
  solutions: string[];
  whyUs: string;
  photos: { src: string; alt: string }[];
  relatedLinks: { label: string; href: string }[];
}

const ServicePageTemplate = ({ data }: { data: ServicePageData }) => (
  <Layout>
    <SeoHead title={data.pageTitle} description={data.metaDesc} />
    <JsonLd
      data={[
        serviceSchema({
          name: data.title,
          description: data.metaDesc,
          path: data.path,
          serviceType: data.h1,
        }),
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: data.title, path: data.path },
        ]),
      ]}
    />
    <section className="bg-primary">
      <div className="container py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4 border-l-0 pl-0">{data.h1}</h1>
        <p className="text-primary-foreground/80 max-w-2xl text-pretty">{data.intro}</p>
      </div>
    </section>

    {/* Hero photo */}
    <section className="container -mt-6 mb-12">
      <div className="rounded-sm overflow-hidden shadow-elevated max-w-4xl">
        <img src={data.heroImg} alt={data.heroAlt} className="w-full h-auto max-h-[400px] object-cover" loading="eager" />
      </div>
    </section>

    <section className="container pb-16 md:pb-24 max-w-4xl">
      <h2 className="text-2xl font-black mb-6">Symptoms You May Notice</h2>
      <ul className="space-y-3 mb-12">
        {data.symptoms.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-muted-foreground text-pretty">
            <span className="w-2 h-2 rounded-full bg-accent mt-2 shrink-0" />
            {s}
          </li>
        ))}
      </ul>

      <h2 className="text-2xl font-black mb-6">Common Causes</h2>
      <ul className="space-y-3 mb-12">
        {data.causes.map((c, i) => (
          <li key={i} className="flex items-start gap-3 text-muted-foreground text-pretty">
            <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
            {c}
          </li>
        ))}
      </ul>

      {/* Inline photos */}
      {data.photos.length > 0 && (
        <div className="grid grid-cols-2 gap-4 my-12">
          {data.photos.map((photo, i) => (
            <div key={i} className="rounded-sm overflow-hidden shadow-card aspect-[4/3]">
              <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      )}

      <h2 className="text-2xl font-black mb-6">How We Fix It</h2>
      <ul className="space-y-3 mb-12">
        {data.solutions.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-muted-foreground text-pretty">
            <span className="w-2 h-2 rounded-full bg-accent mt-2 shrink-0" />
            {s}
          </li>
        ))}
      </ul>

      <h2 className="text-2xl font-black mb-4">Why Choose Semper Fi?</h2>
      <p className="text-muted-foreground text-pretty leading-relaxed mb-8">{data.whyUs}</p>

      <div className="bg-primary rounded-sm p-8 text-center">
        <h3 className="text-xl font-bold text-primary-foreground mb-4 border-l-0 pl-0">Ready to Get This Fixed?</h3>
        <Button asChild variant="emergency" size="xl">
          <a href={PHONE_HREF}><Phone className="w-5 h-5" />Call {PHONE_NUMBER}</a>
        </Button>
      </div>

      {data.relatedLinks.length > 0 && (
        <div className="mt-12">
          <h3 className="font-bold mb-4 border-l-0 pl-0">Related Services</h3>
          <div className="flex flex-wrap gap-3">
            {data.relatedLinks.map((link) => (
              <Link key={link.href} to={link.href} className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1">
                {link.label} <ArrowRight className="w-3 h-3" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Geo SEO internal links */}
      <div className="mt-12">
        <h3 className="font-bold mb-4">Serving Homeowners Across</h3>
        <div className="flex flex-wrap gap-2">
          {allGeoTowns.map(town => (
            <Link key={town.slug} to={town.href} className="text-xs text-muted-foreground bg-secondary hover:bg-accent/10 hover:text-accent px-3 py-1.5 rounded-lg transition-colors">
              {town.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  </Layout>
);

export const WellPumpRepair = () => <ServicePageTemplate data={{
  title: "Well Pump Repair Poconos",
  path: "/services/well-pump-repair",
  pageTitle: "Well Pump Repair – Same-Day Diagnosis | Semper Fi Pumps",
  metaDesc: "Well pump stopped working? We diagnose and repair all pump types same-day in the Pocono Mountains. 33 years experience. Call now.",
  h1: "Pocono Mountains Well Pump Repair",
  intro: "When your well pump fails, you need a technician who knows Pocono Mountain wells inside and out. We diagnose and repair all pump types fast.",
  heroImg: wellCasingImg,
  heroAlt: "Well casing exposed during pump repair showing pipe connections and wiring in the Pocono Mountains",
  symptoms: ["No water coming from any tap in your home", "Sputtering or air in water lines", "Pump runs but produces no water", "Circuit breaker trips repeatedly", "Low water pressure throughout the house"],
  causes: ["Failed pump motor or worn impeller", "Electrical issues: bad capacitor, wiring, or control box", "Pressure switch malfunction", "Dropped water table or collapsed well casing", "Waterlogged pressure tank"],
  solutions: ["Complete pump motor testing and replacement", "Electrical diagnostics and component repair", "Pressure switch adjustment and replacement", "Pressure tank evaluation and replacement", "Full system inspection with honest recommendations"],
  whyUs: "With 33 years repairing well pumps across Monroe, Pike, Carbon, and Northampton Counties, we've seen every failure mode. As a factory-authorized Franklin Electric distributor and a veteran-owned business, we give you a straight diagnosis and fair price — no upselling, no unnecessary work.",
  photos: [
    { src: crewPullingImg, alt: "Semper Fi technicians working together to pull a submersible well pump for repair" },
    { src: openWellImg, alt: "Open well showing water level and submersible pump connections during repair inspection" },
  ],
  relatedLinks: [
    { label: "Well Pump Installation", href: "/services/well-pump-installation" },
    { label: "Emergency Service", href: "/services/emergency-pump-service" },
    { label: "Water Filtration", href: "/services/water-filtration" },
  ],
}} />;

export const WellPumpInstallation = () => <ServicePageTemplate data={{
  title: "Well Pump Installation Poconos",
  path: "/services/well-pump-installation",
  pageTitle: "Well Pump Installation – Pocono Mountains | Semper Fi",
  metaDesc: "Professional well pump installation sized for Pocono Mountain wells. Submersible, jet, and constant pressure systems. Veteran-owned, 33 years. Call today.",
  h1: "Pocono Mountains Well Pump Installation",
  intro: "When repair isn't enough, we install new well pump systems designed to last in Pocono Mountain conditions.",
  heroImg: wellPullingImg,
  heroAlt: "Professional well pump installation equipment used for submersible pump lowering into well casing",
  symptoms: ["Pump has failed and is beyond repair", "System is 15+ years old with declining performance", "You're building a new home with a well", "Current pump can't keep up with household demand", "Frequent costly repairs on an aging system"],
  causes: ["End of pump lifespan (typically 8-15 years)", "Incorrectly sized pump for your well depth and demand", "Corrosion from aggressive well water", "Lightning or power surge damage"],
  solutions: ["Proper well assessment and pump sizing", "Professional submersible pump installation", "Jet pump installation for shallow wells", "Constant pressure system installation", "Complete system startup and testing"],
  whyUs: "We install pumps every week in the Poconos. As a factory-authorized Franklin Electric distributor and warranty center, we install the most trusted pumps in the industry — properly sized and configured for Pocono Mountain wells. Every installation comes with our veteran's guarantee of quality.",
  photos: [
    { src: crewPullingImg, alt: "Technicians extracting old well pump during professional pump replacement service" },
    { src: excavatorDigImg, alt: "Kubota excavator digging for new well pump installation at Pocono Mountains property" },
  ],
  relatedLinks: [
    { label: "Well Pump Repair", href: "/services/well-pump-repair" },
    { label: "Water Filtration", href: "/services/water-filtration" },
  ],
}} />;

export const GrinderPumpRepair = () => <ServicePageTemplate data={{
  title: "Grinder Pump Sales & Service Poconos",
  path: "/services/grinder-pump-repair",
  pageTitle: "Grinder Pump Sales, Service & Install | Semper Fi Pumps",
  metaDesc: "Residential & commercial grinder pump sales, installation, and repair across the Pocono Mountains. Factory-authorized Franklin Electric distributor. Call now.",
  h1: "Residential & Commercial Grinder Pump Sales, Service & Installation",
  intro: "Grinder pumps are the heart of our business. From single-family homes to commercial pump stations, we sell, install, and repair grinder pumps across the Pocono Mountains — and we respond fast when alarms sound.",
  heroImg: grinderInstallImg,
  heroAlt: "Excavator placing grinder pump station during installation at a Pocono Mountains residential property",
  symptoms: ["Grinder pump alarm is sounding", "Sewage backing up into your home or facility", "Foul odor near the grinder pump station", "Pump running continuously without pumping", "Slow drains throughout the building"],
  causes: ["Failed grinder pump motor", "Clogged grinder mechanism from non-flushable items", "Float switch failure", "Electrical control panel issues", "Damaged discharge piping or check valve"],
  solutions: ["New residential & commercial grinder pump sales", "Complete grinder pump diagnostics and repair", "Pump motor replacement", "Commercial pump station sales, service, and installation", "Float switch and control panel repair", "Full system inspection and preventive maintenance"],
  whyUs: "Grinder pumps are roughly 60% of what we do. Whether it's a single residential basin or a multi-pump commercial station, we have the parts, the equipment, and the experience to get it done right. As a factory-authorized Franklin Electric distributor and warranty center, we sell and install the industry's most reliable grinder pump systems — with full manufacturer-backed warranty support.",
  photos: [
    { src: pumpCloseupImg, alt: "Close-up of sewage grinder pump unit showing pipe connections and motor housing" },
    { src: gravelBackfillImg, alt: "Technician backfilling gravel around grinder pump basin during professional installation" },
    { src: pumpBasinPipingImg, alt: "Grinder pump basin with discharge piping and electrical connections in excavated trench" },
    { src: crewInstallingImg, alt: "Semper Fi crew members installing grinder pump basin below grade at residential property" },
  ],
  relatedLinks: [
    { label: "Sewage Pump Systems", href: "/services/sewage-pump-systems" },
    { label: "Emergency Service", href: "/services/emergency-pump-service" },
  ],
}} />;

export const SewagePumpSystems = () => <ServicePageTemplate data={{
  title: "Sewage Pump Systems Poconos",
  path: "/services/sewage-pump-systems",
  pageTitle: "Sewage Pump Systems – Pocono Mountains | Semper Fi",
  metaDesc: "Sewage pump repair, installation, and replacement in the Pocono Mountains. Fast response to sewage emergencies. Veteran-owned, 33 years. Call today.",
  h1: "Pocono Mountains Sewage Pump Systems",
  intro: "Complete sewage and wastewater pump service for homes and commercial properties across the Pocono Mountains. From repair to full system replacement.",
  heroImg: sewagePumpImg,
  heroAlt: "Sewage pump basin being lifted into excavated position for residential wastewater system installation",
  symptoms: ["Sewage pump alarm activated", "Wastewater not being pumped out", "Gurgling or slow drains", "Sewage odor in or around your home", "Pump cycling on and off rapidly"],
  causes: ["Pump motor failure", "Blocked or damaged impeller", "Check valve failure", "Float switch malfunction", "Pipe blockage or break"],
  solutions: ["Sewage pump repair and replacement", "Check valve installation", "Float switch replacement", "Commercial sewage pump station service", "System evaluation and upgrade", "Preventive maintenance programs"],
  whyUs: "Sewage pump problems demand immediate, competent response. Our 33 years of experience in the Pocono Mountains means we've handled every type of sewage system issue — residential and commercial. We show up fast, diagnose accurately, and fix it right.",
  photos: [
    { src: crewInstallingImg, alt: "Crew installing sewage pump basin underground at Pocono Mountains residential site" },
    { src: pumpCloseupImg, alt: "Sewage pump unit close-up showing inlet and outlet pipe connections" },
    { src: pumpBasinPipingImg, alt: "Sewage pump basin with completed piping connections before backfill" },
    { src: gravelBackfillImg, alt: "Worker placing gravel drainage base around sewage pump station during installation" },
  ],
  relatedLinks: [
    { label: "Grinder Pump Sales & Service", href: "/services/grinder-pump-repair" },
    { label: "Emergency Service", href: "/services/emergency-pump-service" },
  ],
}} />;

export const WaterFiltration = () => <ServicePageTemplate data={{
  title: "Water Filtration Poconos",
  path: "/services/water-filtration",
  pageTitle: "Well Water Filtration – Iron, Sulfur, Hardness | Semper Fi",
  metaDesc: "Well water filtration systems for iron, sulfur, hardness, and low pH. Tested and installed by Pocono Mountain well water experts. Call for a water test.",
  h1: "Pocono Mountains Water Filtration Systems",
  intro: "Well water in the Poconos can carry iron, sulfur, hardness, and other contaminants. We install filtration systems that deliver clean, safe water.",
  heroImg: openWellImg,
  heroAlt: "Open well showing water quality conditions requiring filtration and treatment in the Pocono Mountains",
  symptoms: ["Rotten egg smell from your water", "Orange/brown staining on fixtures", "Hard water scale buildup", "Cloudy or discolored water", "Metallic taste in drinking water"],
  causes: ["High iron or manganese content", "Hydrogen sulfide gas (sulfur)", "Hard water minerals (calcium, magnesium)", "Sediment from the well", "Low pH (acidic water) common in the Poconos"],
  solutions: ["Water testing and analysis", "Whole-house iron and sulfur filtration", "Water softener installation", "UV disinfection systems", "Acid neutralizer installation for low pH"],
  whyUs: "We know Pocono Mountain well water. The geology here creates specific water quality challenges that generic solutions can't address. With 33 years of local experience, we test your water, recommend the right system, and install it properly.",
  photos: [],
  relatedLinks: [
    { label: "Well Pump Repair", href: "/services/well-pump-repair" },
    { label: "Well Pump Installation", href: "/services/well-pump-installation" },
  ],
}} />;

export const EmergencyPumpService = () => <ServicePageTemplate data={{
  title: "Emergency Well Pump Repair Poconos",
  path: "/services/emergency-pump-service",
  pageTitle: "Emergency Pump Service – Same-Day Response | Semper Fi",
  metaDesc: "Lost water? We respond same-day to emergency pump failures throughout the Pocono Mountains. Parts on the truck. Veteran-owned, 33 years. Call now.",
  h1: "Emergency Pump Service — Pocono Mountains",
  intro: "When you have no water, every hour counts. We prioritize emergency calls and respond same-day throughout the Pocono Mountains.",
  heroImg: truckImg,
  heroAlt: "Semper Fi emergency well pump service truck fully equipped for same-day pump repair response",
  symptoms: ["Complete loss of water", "Pump making loud or unusual noises", "Burning smell from the pump", "Water suddenly turned brown or cloudy", "Grinder pump alarm sounding"],
  causes: ["Pump motor burnout", "Lightning strike or power surge", "Frozen pipes or components", "Well casing collapse", "Electrical failure"],
  solutions: ["Rapid on-site diagnostics", "Emergency pump replacement from truck stock", "Temporary water solutions", "Electrical repair and surge protection", "After-hours and weekend emergency response"],
  whyUs: "We live here. When a storm knocks out pumps across the Poconos, we're already in the area. With 33 years of emergency response, we carry the most common parts on our trucks — including factory-authorized Franklin Electric pumps — so we can get your water back the same day.",
  photos: [
    { src: wellPullingImg, alt: "Emergency well pump extraction using portable pulling equipment for fast pump replacement" },
    { src: wellCasingImg, alt: "Well casing access during emergency pump repair showing underground infrastructure" },
  ],
  relatedLinks: [
    { label: "Well Pump Repair", href: "/services/well-pump-repair" },
    { label: "Grinder Pump Sales & Service", href: "/services/grinder-pump-repair" },
  ],
}} />;

export const HydroFracking = () => <ServicePageTemplate data={{
  title: "Hydro-Fracking Poconos",
  path: "/services/hydro-fracking",
  pageTitle: "Hydro-Fracking for Low Yielding Wells | Semper Fi",
  metaDesc: "Hydro-fracking service in the Pocono Mountains for low yielding wells and dry wells. Restore water flow without drilling a new well. Veteran-owned. Call today.",
  h1: "Hydro-Fracking — Low Yielding Wells or Dry Wells",
  intro: "If your well isn't producing enough water, hydro-fracking can restore flow without the cost of drilling a brand-new well. We use high-pressure water to open up existing fractures in the bedrock and create new pathways for water to enter your well.",
  heroImg: pumpStationImg,
  heroAlt: "Well rig and pump station equipment for hydro-fracking low yielding and dry wells in the Pocono Mountains",
  symptoms: ["Well runs dry during heavy household use", "Recovery time between uses is very long", "Pump short-cycles or sucks air", "Water yield has declined over the years", "Newly drilled well is producing less than expected"],
  causes: ["Naturally low-yielding bedrock formations", "Sediment or mineral deposits clogging existing fractures", "Seasonal water table drops in the Pocono Plateau", "Aging well that has lost productivity over time"],
  solutions: ["Full well assessment and yield testing", "High-pressure hydro-fracking to open existing fractures", "Creation of new water pathways in surrounding bedrock", "Post-fracking yield testing and verification", "Pump system re-evaluation and upgrade if needed"],
  whyUs: "Hydro-fracking is a specialized service that requires the right equipment and the experience to know when it will work. With 33 years of Pocono Mountain well experience, we know which wells respond well to hydro-fracking — and we'll tell you straight if it's not the right solution for yours.",
  photos: [
    { src: excavatorDigImg, alt: "Heavy equipment used for well work and hydro-fracking in the Pocono Mountains" },
    { src: openWellImg, alt: "Open well showing existing water level and bedrock conditions before hydro-fracking" },
  ],
  relatedLinks: [
    { label: "Well Pump Installation", href: "/services/well-pump-installation" },
    { label: "Well Pump Repair", href: "/services/well-pump-repair" },
  ],
}} />;
